import { Fragment, ReactElement, useCallback, useMemo, MouseEvent, useRef } from 'react'
import { elementTypeToLabelSingular } from '@domainConstants'
import { filter, groupBy, mapValues, sortBy } from 'lodash'
import { Vector2, Vector3 } from 'three'
import { Close } from '@mui/icons-material'
import {
  Box,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { Html } from '@react-three/drei'
import { useTriggerContextMenu } from '@ui/hooks'
import { useControlStore } from '@editorStores'
import { useCanvasEvent, useElementLabel, useGuidToElementIncludingRips } from '@editorHooks'
import { SceneContextMenuAction } from './types'

const Context = (): ReactElement => {
  const theme = useTheme()

  const addHiddenElementIds = useControlStore(state => state.addHiddenElementIds)
  const sceneContextMenuActions = useControlStore(state => state.sceneContextMenuActions)
  const setPropertiesPopupSelectedElement = useControlStore(
    state => state.setPropertiesPopupSelectedElement,
  )

  const { closeContextMenu, context } = useTriggerContextMenu()

  const selectedElementName = context?.elementId

  const getLabel = useElementLabel()

  const guidToElement = useGuidToElementIncludingRips()

  const element = selectedElementName ? guidToElement[selectedElementName] : null

  const actionGroups = useMemo(() => {
    const defaultActions: SceneContextMenuAction[] = [
      {
        id: 'hide',
        label: userDataActions => {
          if ('hide' in userDataActions) {
            return userDataActions.hide.label || 'Verstecken'
          }
          return 'Verstecken'
        },
        action: (selectedElement, point, event, userDataActions) => {
          if (selectedElement) {
            if ('hide' in userDataActions) {
              addHiddenElementIds([userDataActions.hide.guid])
            }
          }
        },
        available: (selectedElement, point, userDataActions) => {
          return !!selectedElement && 'hide' in userDataActions
        },
        group: 'default',
      },
      {
        id: 'properties',
        label: userDataActions => {
          if ('properties' in userDataActions) {
            return userDataActions.properties.label || 'Eigenschaften'
          }
          return 'Eigenschaften'
        },
        action: (selectedElement, point, event, userDataActions) => {
          if (selectedElement && 'properties' in userDataActions) {
            setPropertiesPopupSelectedElement(userDataActions.properties.guid)
          }
        },
        available: (selectedElement, point, userDataActions) => {
          return !!selectedElement && 'properties' in userDataActions
        },
        group: 'default',
      },
      {
        id: 'properties-wall',
        label: userDataActions => {
          if ('propertiesWall' in userDataActions) {
            return userDataActions.propertiesWall.label || 'Eigenschaften (Wand)'
          }
          return 'Eigenschaften (Wand)'
        },
        action: (selectedElement, point, event, userDataActions) => {
          if (selectedElement && 'propertiesWall' in userDataActions) {
            setPropertiesPopupSelectedElement(userDataActions.propertiesWall.guid)
          }
        },
        available: (selectedElement, point, userDataActions) => {
          return !!selectedElement && 'propertiesWall' in userDataActions
        },
        group: 'default',
      },
    ]

    const allActions = [...defaultActions, ...sceneContextMenuActions]

    const enabledActions = filter(allActions, action => {
      if (action.available) {
        return action.available(
          context?.elementId || null,
          context?.point || null,
          context?.actions || {},
          context?.userData || {},
        )
      }

      return true
    })

    const groupedActions = groupBy(enabledActions, 'group')

    const sortedActions = mapValues(groupedActions, actions => sortBy(actions, 'label'))

    return sortedActions
  }, [
    sceneContextMenuActions,
    addHiddenElementIds,
    setPropertiesPopupSelectedElement,
    context?.elementId,
    context?.point,
    context?.actions,
    context?.userData,
  ])

  const mouseStartPosition = useRef<Vector2>(new Vector2())

  const handleMouseDown = useCallback((event: HTMLElementEventMap['mousedown']) => {
    if (event.button !== 2) return
    mouseStartPosition.current = new Vector2(event.clientX, event.clientY)
  }, [])

  useCanvasEvent('mousedown', handleMouseDown)

  const handleMouseUp = useCallback(
    (event: HTMLElementEventMap['pointerup']) => {
      if (event.button === 0 && context) {
        closeContextMenu()
      }
    },
    [context, closeContextMenu],
  )

  useCanvasEvent('pointerup', handleMouseUp)

  const onClickAction = (action: SceneContextMenuAction, event: MouseEvent) => {
    action.action?.(
      context?.elementId || null,
      context?.point as Vector3,
      event,
      context?.actions || {},
      context?.userData || {},
    )
    closeContextMenu()
  }

  const elementTypeLabel = useMemo(() => {
    if (!element) return null
    if (!('element_type' in element)) return null
    if (!element.element_type) return null

    return elementTypeToLabelSingular[element.element_type] || null
  }, [element])

  const storeyLabel = useMemo(() => {
    if (!element) return null
    if (!('storey' in element)) return null
    if (!element.storey) return null
    return element.storey
  }, [element])

  const elementLabel = useMemo(() => {
    if (!selectedElementName || selectedElementName === '') return 'n/a'

    return getLabel(selectedElementName)
  }, [getLabel, selectedElementName])

  return (
    <>
      {context && (
        <Html position={context?.point || undefined} visible={context !== null}>
          <Paper onClick={event => event.stopPropagation()}>
            <Stack direction="column" spacing={0} divider={<Divider />}>
              {context?.elementId && (
                <Box
                  p={2}
                  pt={1}
                  pb={1}
                  sx={{
                    backgroundColor: theme.palette.grey[200],
                    borderTopLeftRadius: 5,
                    borderTopRightRadius: 5,
                    minWidth: 200,
                    cursor: 'default',
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography fontWeight="bold" fontSize={16}>
                      {elementLabel}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={event => {
                        event.stopPropagation()
                        closeContextMenu()
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Stack>
                  {storeyLabel && <Typography fontSize={14}>Stockwerk: {storeyLabel}</Typography>}
                  {elementTypeLabel && <Typography fontSize={14}>{elementTypeLabel}</Typography>}
                </Box>
              )}
              {Object.keys(actionGroups).length > 0 ? (
                Object.entries(actionGroups).map(([group, actions]) => (
                  <Fragment key={group}>
                    {actions.map(action =>
                      action.render ? (
                        <Box key={action.id} onClick={event => event.stopPropagation()}>
                          {action.render({
                            selectedElement: context?.elementId || null,
                            point: context?.point || null,
                            userDataActions: context?.actions || {},
                            userData: context?.userData || {},
                            close: closeContextMenu,
                          })}
                        </Box>
                      ) : (
                        <MenuItem
                          key={action.id}
                          onClick={event => onClickAction(action, event)}
                          sx={{ fontSize: 15 }}
                        >
                          {typeof action.label === 'string'
                            ? action.label
                            : action.label?.(context?.actions || {})}
                        </MenuItem>
                      ),
                    )}
                  </Fragment>
                ))
              ) : (
                <Box p={2}>
                  <Typography fontSize={15}>Keine Aktionen verfügbar</Typography>
                </Box>
              )}
            </Stack>
          </Paper>
        </Html>
      )}
    </>
  )
}

export default Context
